import apiV2 from '../api/v2';
import frontBuccalRef from '../assets/images/spark/front_buccal.jpg';
import rightBuccalRef from '../assets/images/spark/right_buccal.jpg';
import leftBuccalRef from '../assets/images/spark/left_buccal.jpg';
import upperOcclusalRef from '../assets/images/spark/upper_occlusal.jpg';
import lowerOcclusalRef from '../assets/images/spark/lower_occlusal.jpg';
import profileRightRef from '../assets/images/spark/right_profile.jpg';
import profileLeftRef from '../assets/images/spark/left_profile.jpg';
import frontalRestRef from '../assets/images/spark/frontal_rest.jpg';
import frontalSmileRef from '../assets/images/spark/frontal_smile.jpg';

export const SPARK_PHOTO_POSITIONS = [
    'EXTRAORAL_FRONTAL_REST',
    'EXTRAORAL_FRONTAL_SMILING',
    'EXTRAORAL_PROFILE_LEFT',
    'EXTRAORAL_PROFILE_RIGHT',
    'INTRAORAL_FRONTAL',
    'INTRAORAL_BUCCAL_LEFT',
    'INTRAORAL_BUCCAL_RIGHT',
    'INTRAORAL_OCCLUSAL_LOWER',
    'INTRAORAL_OCCLUSAL_UPPER',
    'XRAY_LATERAL',
    'XRAY_PANORAMIC'
] as const;

export type SparkPhotoPosition = (typeof SPARK_PHOTO_POSITIONS)[number];

export const SPARK_PHOTO_POSITION_LABELS: Record<SparkPhotoPosition, string> = {
    EXTRAORAL_FRONTAL_REST: 'Extraoral · Frontal (rest)',
    EXTRAORAL_FRONTAL_SMILING: 'Extraoral · Frontal (smiling)',
    EXTRAORAL_PROFILE_LEFT: 'Extraoral · Profile (left)',
    EXTRAORAL_PROFILE_RIGHT: 'Extraoral · Profile (right)',
    INTRAORAL_FRONTAL: 'Intraoral · Frontal',
    INTRAORAL_BUCCAL_LEFT: 'Intraoral · Buccal (left)',
    INTRAORAL_BUCCAL_RIGHT: 'Intraoral · Buccal (right)',
    INTRAORAL_OCCLUSAL_LOWER: 'Intraoral · Occlusal (lower)',
    INTRAORAL_OCCLUSAL_UPPER: 'Intraoral · Occlusal (upper)',
    XRAY_LATERAL: 'X-ray · Lateral',
    XRAY_PANORAMIC: 'X-ray · Panoramic'
};

/**
 * The composite-grid slots shown in the photos step, modeled on the dashboard's
 * patient-creation photo layout. Order = grid order. Each slot maps 1:1 to a
 * Spark PhotoPosition; x-ray/composite slots have no reference image.
 */
export interface SparkPhotoSlot {
    position: SparkPhotoPosition;
    label: string;
    referenceImage: string | null;
}

export const SPARK_PHOTO_SLOTS: SparkPhotoSlot[] = [
    { position: 'EXTRAORAL_FRONTAL_REST', label: 'Frontal rest', referenceImage: frontalRestRef },
    {
        position: 'EXTRAORAL_FRONTAL_SMILING',
        label: 'Frontal smile',
        referenceImage: frontalSmileRef
    },
    {
        position: 'EXTRAORAL_PROFILE_RIGHT',
        label: 'Profile right',
        referenceImage: profileRightRef
    },
    { position: 'EXTRAORAL_PROFILE_LEFT', label: 'Profile left', referenceImage: profileLeftRef },
    { position: 'INTRAORAL_FRONTAL', label: 'Front buccal', referenceImage: frontBuccalRef },
    { position: 'INTRAORAL_BUCCAL_RIGHT', label: 'Right buccal', referenceImage: rightBuccalRef },
    { position: 'INTRAORAL_BUCCAL_LEFT', label: 'Left buccal', referenceImage: leftBuccalRef },
    {
        position: 'INTRAORAL_OCCLUSAL_UPPER',
        label: 'Upper occlusal',
        referenceImage: upperOcclusalRef
    },
    {
        position: 'INTRAORAL_OCCLUSAL_LOWER',
        label: 'Lower occlusal',
        referenceImage: lowerOcclusalRef
    },
    { position: 'XRAY_LATERAL', label: 'X-ray lateral', referenceImage: null },
    { position: 'XRAY_PANORAMIC', label: 'X-ray panoramic', referenceImage: null }
];

// Dashboard patient-creation photoType ids → Spark positions (for auto-prefill).
const PHOTO_TYPE_TO_POSITION: Record<string, SparkPhotoPosition> = {
    front_buccal: 'INTRAORAL_FRONTAL',
    right_buccal: 'INTRAORAL_BUCCAL_RIGHT',
    left_buccal: 'INTRAORAL_BUCCAL_LEFT',
    upper_occlusal: 'INTRAORAL_OCCLUSAL_UPPER',
    lower_occlusal: 'INTRAORAL_OCCLUSAL_LOWER',
    profile_right: 'EXTRAORAL_PROFILE_RIGHT',
    profile_left: 'EXTRAORAL_PROFILE_LEFT',
    frontal_rest: 'EXTRAORAL_FRONTAL_REST',
    frontal_smile: 'EXTRAORAL_FRONTAL_SMILING'
};

/** Resolve a stored photoType (dashboard id OR a raw Spark position) to a slot position. */
export function positionForPhotoType(
    photoType: string | null | undefined
): SparkPhotoPosition | null {
    if (!photoType) return null;
    if ((SPARK_PHOTO_POSITIONS as readonly string[]).includes(photoType)) {
        return photoType as SparkPhotoPosition;
    }
    return PHOTO_TYPE_TO_POSITION[photoType] ?? null;
}

/**
 * Resolve a patient file to its Spark slot. Prefers the explicit photoType
 * metadata; falls back to the coarse fileType for radiographs, which are
 * uploaded as bare `RADIOGRAPH` with no photoType/metadata. At this point in
 * the flow the only radiograph on file is the panoramic (the lateral ceph is
 * uploaded later), so an unannotated radiograph maps to the panoramic slot.
 */
export function positionForFile(file: {
    photoType?: string | null;
    fileType?: string | null;
}): SparkPhotoPosition | null {
    const byPhotoType = positionForPhotoType(file.photoType);
    if (byPhotoType) return byPhotoType;
    if (file.fileType === 'RADIOGRAPH') return 'XRAY_PANORAMIC';
    return null;
}

const sparkBase = (patientId: string): string =>
    `/patients/${patientId}/workflow/design/manufacturer/spark`;

// create-refinement lives under the refinement work-queue step (Ref-2.2), not
// design — its photo/scan uploads still reuse the shared `sparkBase` endpoints.
const sparkRefinementBase = (patientId: string): string =>
    `/patients/${patientId}/workflow/refinement/manufacturer/spark`;

export interface SparkCaseMatch {
    caseId: number;
    status: string;
    firstName: string | null;
    lastName: string | null;
}

/**
 * Step 1 - search Spark for an existing case (link candidates). With no params
 * it searches by the patient's own record; pass `firstName`/`lastName` to search
 * by an arbitrary name instead.
 */
export async function searchSparkCases(
    patientId: string,
    params?: { firstName?: string; lastName?: string }
): Promise<{ matches: SparkCaseMatch[] }> {
    const response = await apiV2.get<{ success: boolean; data: { matches: SparkCaseMatch[] } }>(
        `${sparkBase(patientId)}/search-cases`,
        { params }
    );
    return response.data.data;
}

/** Step 1 (alt) - link an existing Spark case to this patient. */
export async function linkSparkCase(patientId: string, caseId: number): Promise<SparkCaseResult> {
    const response = await apiV2.post<{ success: boolean; data: SparkCaseResult }>(
        `${sparkBase(patientId)}/link-case`,
        { caseId }
    );
    return response.data.data;
}

/** Step 1 (undo) - detach the linked/created case from this patient. */
export async function unlinkSparkCase(patientId: string): Promise<{ unlinked: true }> {
    const response = await apiV2.post<{ success: boolean; data: { unlinked: true } }>(
        `${sparkBase(patientId)}/unlink-case`,
        {}
    );
    return response.data.data;
}

export interface SparkCaseResult {
    caseId: string;
    link: string;
}

export async function createSparkCase(patientId: string): Promise<SparkCaseResult> {
    const response = await apiV2.post<{ success: boolean; data: SparkCaseResult }>(
        `${sparkBase(patientId)}/create-case`,
        {}
    );
    return response.data.data;
}

/**
 * Create a refinement case in Spark off the patient's stored (parent) case.
 * Returns the NEW refinement case id + link; the caller holds it in session
 * state and passes it as `caseId` to the photo/scan uploads (it is not
 * persisted server-side).
 */
export async function createSparkRefinement(patientId: string): Promise<SparkCaseResult> {
    const response = await apiV2.post<{ success: boolean; data: SparkCaseResult }>(
        `${sparkRefinementBase(patientId)}/create-refinement`,
        {}
    );
    return response.data.data;
}

/**
 * Detect an already-created but UNSUBMITTED refinement for this patient, so the
 * wizard can resume it instead of creating a duplicate. Returns the existing
 * refinement's case id + link, or `null` when none is pending.
 */
export async function getUnsubmittedSparkRefinement(
    patientId: string
): Promise<SparkCaseResult | null> {
    const response = await apiV2.get<{
        success: boolean;
        data: { refinement: SparkCaseResult | null };
    }>(`${sparkRefinementBase(patientId)}/unsubmitted-refinement`);
    return response.data.data.refinement;
}

export interface SparkPhotoSelection {
    patientFileId: string;
    position: SparkPhotoPosition;
}

export async function uploadSparkPhotos(
    patientId: string,
    files: SparkPhotoSelection[],
    caseId?: number
): Promise<{ uploaded: number }> {
    const response = await apiV2.post<{ success: boolean; data: { uploaded: number } }>(
        `${sparkBase(patientId)}/upload-photos`,
        caseId !== undefined ? { files, caseId } : { files }
    );
    return response.data.data;
}

export async function uploadSparkScans(
    patientId: string,
    files: { patientFileId: string }[],
    caseId?: number
): Promise<{ uploaded: number }> {
    const response = await apiV2.post<{ success: boolean; data: { uploaded: number } }>(
        `${sparkBase(patientId)}/upload-scans`,
        caseId !== undefined ? { files, caseId } : { files }
    );
    return response.data.data;
}
